import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, RotateCcw, ArrowRight, Award } from "lucide-react";
import { QUIZ_QUESTIONS, SCORE_MESSAGES, IMAGES } from "@/data/content";
import { OrnamentalDivider } from "@/components/OrnamentalDivider";

export default function Quiz() {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const score = QUIZ_QUESTIONS.reduce(
    (n, q) => (answers[q.id] === q.correct ? n + 1 : n),
    0,
  );
  const allAnswered = Object.keys(answers).length === QUIZ_QUESTIONS.length;
  const scoreMsg = SCORE_MESSAGES[score] ?? SCORE_MESSAGES[0];

  const handleAnswer = (qid, option) => {
    if (submitted) return;
    setAnswers((a) => ({ ...a, [qid]: option }));
  };

  const submit = () => {
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const retake = () => {
    setAnswers({});
    setSubmitted(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid="quiz-page">
      {/* HERO */}
      <section className="relative h-[36vh] min-h-[300px] overflow-hidden">
        <img
          src={IMAGES.parliament}
          alt="Parliament House"
          className="absolute inset-0 w-full h-full object-cover sepia-soft"
        />
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-10">
          <div className="stamp bg-[#FDFBF7]/85 w-fit">Marvellous Melbourne Quiz</div>
          <h1 className="serif text-white text-5xl md:text-6xl mt-4">
            Think you know Melbourne?
          </h1>
          <p className="text-[#F5EFEB] mt-3 max-w-2xl">
            Ten questions from Martin. Multiple choice. No pressure — except your own.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        {submitted && (
          <ResultCard score={score} scoreMsg={scoreMsg} onRetake={retake} />
        )}

        <div className="space-y-8 mt-8">
          {QUIZ_QUESTIONS.map((q, idx) => (
            <QuestionCard
              key={q.id}
              q={q}
              idx={idx}
              selected={answers[q.id]}
              onSelect={(o) => handleAnswer(q.id, o)}
              submitted={submitted}
            />
          ))}
        </div>

        {!submitted && (
          <div className="mt-10 text-center">
            <button
              onClick={submit}
              disabled={!allAnswered}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-quiz"
            >
              {allAnswered ? (
                <>
                  Reveal my score <ArrowRight size={16} />
                </>
              ) : (
                `Answer all 10 to continue (${Object.keys(answers).length}/10)`
              )}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

function QuestionCard({ q, idx, selected, onSelect, submitted }) {
  return (
    <div
      className="card-warm rounded-2xl p-6 md:p-8"
      data-testid={`quiz-question-${q.id}`}
    >
      <div className="text-xs uppercase tracking-[0.24em] text-[#1E4637]">
        Question {idx + 1} of 10
      </div>
      <h3 className="serif text-2xl text-[#6E2638] mt-2 leading-snug">
        {q.text}
      </h3>
      <div className="mt-5 space-y-2">
        {q.options.map((opt) => {
          const isSelected = selected === opt.key;
          const isCorrect = submitted && opt.key === q.correct;
          const isWrong = submitted && isSelected && opt.key !== q.correct;

          let cls =
            "w-full text-left px-4 py-3 rounded-xl border-2 transition-all flex items-center gap-3 ";
          if (isCorrect) cls += "bg-[#1E4637] text-[#FDFBF7] border-[#1E4637]";
          else if (isWrong)
            cls += "bg-[#6E2638]/10 text-[#6E2638] border-[#6E2638]";
          else if (isSelected && !submitted)
            cls += "bg-[#F5EFEB] border-[#6E2638]";
          else if (submitted) cls += "bg-[#FDFBF7] border-[#E5D9C5] opacity-60";
          else
            cls +=
              "bg-[#FDFBF7] border-[#E5D9C5] hover:border-[#6E2638]/50 hover:bg-[#F5EFEB]/60";

          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onSelect(opt.key)}
              disabled={submitted}
              data-testid={`quiz-option-${q.id}-${opt.key}`}
              className={cls}
            >
              <span className="font-mono uppercase text-sm shrink-0">
                {opt.key}.
              </span>
              <span className="flex-1">{opt.text}</span>
              {isCorrect && <CheckCircle2 size={18} className="shrink-0" />}
              {isWrong && <XCircle size={18} className="shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ResultCard({ score, scoreMsg, onRetake }) {
  return (
    <div
      className="card-warm rounded-2xl p-8 md:p-10 text-center"
      data-testid="quiz-result"
    >
      <Award size={48} className="mx-auto text-[#D4AF37]" />
      <div className="stamp mt-4 mx-auto w-fit">Your Score</div>
      <div
        className="serif text-6xl md:text-8xl text-[#6E2638] mt-3"
        data-testid="quiz-score"
      >
        {score} <span className="text-[#D4AF37]">/</span> 10
      </div>
      <OrnamentalDivider />
      <p
        className="serif italic text-2xl md:text-3xl text-[#1A1817]"
        data-testid="quiz-message"
      >
        &ldquo;{scoreMsg}&rdquo;
      </p>
      <div className="mt-8 flex flex-wrap gap-4 justify-center">
        <button
          onClick={onRetake}
          className="btn-outline-gold"
          data-testid="retake-quiz"
        >
          <RotateCcw size={16} /> Retake quiz
        </button>
        <Link to="/individual" className="btn-primary">
          Book the walk <ArrowRight size={16} />
        </Link>
      </div>
      <p className="text-xs text-[#1A1817]/60 mt-6 italic">
        Scroll down to see the correct answer beside each question.
      </p>
    </div>
  );
}
